import { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, Pencil, Plus, Save, X } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { ReferentialImport } from "../components/ReferentialImport";
import { getAccountTypeRoute, getStoredAccountType } from "../auth";
import {
  downloadCsv,
  fetchActCodes,
  saveActCode,
  type ActCode,
  type ActCodePayload,
  type ActCodesResponse,
} from "../api/referentialsApi";
import { ExportMenu } from "../components/ExportMenu";

type Draft = ActCodePayload & { code: string };

const EMPTY: Draft = { code: "", name: "", category: null, kind: "act", care_category: null, act_group: null, active: true };

const KIND_STYLE: Record<string, string> = {
  act: "bg-blue-50 text-blue-800",
  refaction: "bg-red-50 text-red-700",
  fee: "bg-amber-50 text-amber-800",
};

function payloadOf(act: ActCode, active = act.active): ActCodePayload {
  return { name: act.name, category: act.category, kind: act.kind, care_category: act.care_category, act_group: act.act_group, active };
}

/**
 * Référentiel des codes actes utilisés par l'Audit pour coder les factures : actes (RCS, RB, RPPH…),
 * réfactions (COR…) et frais (RADMIN). Le type d'acte d'un code détermine le taux de couverture du package.
 * Admin et Sinistre modifient ; l'Audit consulte. Un code désactivé n'est plus proposé à la codification.
 */
export function ActCodesPage() {
  const { showToast } = useToast();
  const [data, setData] = useState<ActCodesResponse | null>(null);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    fetchActCodes()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, []);

  useEffect(load, [load]);

  const acts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.acts ?? []).filter(
      (act) =>
        (!kind || act.kind === kind) &&
        (!status || act.active === (status === "1")) &&
        (!term || [act.code, act.name, act.category ?? "", act.care_category_label ?? ""].some((v) => v.toLowerCase().includes(term))),
    );
  }, [data, search, kind, status]);

  const canEdit = data?.can_edit ?? false;
  const withoutType = (data?.acts ?? []).filter((a) => a.kind === "act" && a.active && !a.care_category).length;

  const closeForm = () => {
    setDraft(null);
    setEditing(null);
  };

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      showToast(message);
      load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!draft || !draft.name.trim() || (!editing && !draft.code.trim())) return;
    const { code, ...payload } = draft;
    const ok = await run(
      () => saveActCode(editing ? payload : { ...payload, code: code.trim() }, editing ?? undefined),
      editing ? "Code acte enregistré." : "Code acte ajouté.",
    );
    if (ok) closeForm();
  };

  const exportCsv = () =>
    downloadCsv(
      "codes-actes.csv",
      ["Code", "Nom", "Catégorie", "Nature", "Type d'acte", "Actif"],
      (data?.acts ?? []).map((a) => [a.code, a.name, a.category, a.kind_label, a.care_category_label, a.active ? "oui" : "non"]),
    );

  const statusBadge = (active: boolean) => `rounded-full px-2 py-0.5 text-xs ${active ? "bg-green-100 text-green-800" : "bg-card-muted text-muted"}`;

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumbs homeHref={getAccountTypeRoute(getStoredAccountType() ?? "admin")} items={[{ label: "Référentiels" }, { label: "Codes actes" }]} />

      <section className="theme-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <ListChecks size={20} className="text-primary" />
              Codes actes
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-muted">
              Codes proposés au médecin pour coder chaque acte d'une facture. Le <strong>type d'acte</strong> d'un code
              détermine le taux de couverture et les plafonds du package du patient. Un code désactivé n'est plus proposé,
              mais reste sur les factures déjà codées.
              {canEdit ? "" : " Consultation seule."}
            </p>
            {canEdit && withoutType > 0 ? (
              <p className="mt-2 text-sm text-amber-700">
                {withoutType} code(s) acte sans type d'acte : le taux du package ne leur est pas appliqué.
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <ExportMenu onExport={exportCsv} />
            {canEdit ? (
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setDraft(EMPTY);
                }}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground"
              >
                <Plus size={15} />
                Nouveau code
              </button>
            ) : null}
          </div>
        </div>
        {canEdit ? (
          <div className="mt-4">
            <ReferentialImport kind="acts" columns="Code, Nom (obligatoires), Catégorie, Nature, Type d'acte, Actif" onImported={load} />
          </div>
        ) : null}
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      </section>

      {draft ? (
        <section className="theme-card p-5">
          <h2 className="mb-3 text-base font-semibold text-foreground">{editing ? `Modifier ${editing}` : "Nouveau code acte"}</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <FormField label="Code" required>
              <input
                value={draft.code}
                disabled={Boolean(editing)}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                placeholder="ex. RTELE"
                className={inputClass}
              />
            </FormField>
            <FormField label="Nom" required className="md:col-span-2">
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputClass} />
            </FormField>
            <FormField label="Nature" required>
              <select
                value={draft.kind}
                onChange={(e) => setDraft({ ...draft, kind: e.target.value, care_category: e.target.value === "act" ? draft.care_category : null })}
                className={selectClass}
              >
                {(data?.kinds ?? []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Type d'acte" hint="Sert au taux de couverture du package.">
              <select
                value={draft.care_category ?? ""}
                disabled={draft.kind !== "act"}
                onChange={(e) => setDraft({ ...draft, care_category: e.target.value || null })}
                className={selectClass}
              >
                <option value="">— Aucun —</option>
                {(data?.care_categories ?? []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Groupe (reporting)" hint="Nature d'actes du reporting.">
              <select
                value={draft.act_group ?? ""}
                disabled={draft.kind !== "act"}
                onChange={(e) => setDraft({ ...draft, act_group: e.target.value || null })}
                className={selectClass}
              >
                <option value="">— Autres actes —</option>
                {(data?.act_groups ?? []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Catégorie (libre)">
              <input
                value={draft.category ?? ""}
                onChange={(e) => setDraft({ ...draft, category: e.target.value || null })}
                className={inputClass}
              />
            </FormField>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            Actif (proposé à la codification)
          </label>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              disabled={busy || !draft.name.trim() || (!editing && !draft.code.trim())}
              onClick={() => void handleSave()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
            >
              <Save size={15} />
              Enregistrer
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm text-foreground hover:bg-accent/20 hover:border-accent"
            >
              <X size={15} />
              Annuler
            </button>
          </div>
        </section>
      ) : null}

      <section className="theme-card p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un code, un nom, un type d'acte…"
            className={`${inputClass} max-w-sm`}
          />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={`${selectClass} sm:max-w-[13rem]`}>
            <option value="">Toutes natures</option>
            {(data?.kinds ?? []).map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={`${selectClass} sm:max-w-[13rem]`}>
            <option value="">Actifs et inactifs</option>
            <option value="1">Actifs</option>
            <option value="0">Inactifs</option>
          </select>
          <span className="text-sm text-muted">{acts.length} code(s)</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Nom</th>
                <th className="px-3 py-2">Nature</th>
                <th className="px-3 py-2">Type d'acte</th>
                <th className="px-3 py-2">Groupe (reporting)</th>
                <th className="px-3 py-2">Catégorie</th>
                <th className="px-3 py-2 text-right" title="Lignes de facture codées avec ce code">Utilisé</th>
                <th className="px-3 py-2">Statut</th>
                {canEdit ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {acts.map((act) => (
                <tr key={act.code} className={`border-b border-border ${act.active ? "" : "opacity-60"}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold text-primary">{act.code}</td>
                  <td className="px-3 py-2 text-foreground">{act.name}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${KIND_STYLE[act.kind] ?? ""}`}>{act.kind_label}</span>
                  </td>
                  <td className="px-3 py-2">
                    {act.care_category_label ?? (act.kind === "act" ? <span className="text-xs text-amber-700">Non défini</span> : <span className="text-muted">—</span>)}
                  </td>
                  <td className="px-3 py-2 text-xs text-foreground">{act.act_group_label ?? (act.kind === "act" ? <span className="text-amber-700">Autres actes</span> : <span className="text-muted">—</span>)}</td>
                  <td className="px-3 py-2 text-xs text-muted">{act.category ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted">{act.used}</td>
                  <td className="px-3 py-2">
                    {canEdit ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void run(() => saveActCode(payloadOf(act, !act.active), act.code), act.active ? `${act.code} désactivé.` : `${act.code} réactivé.`)
                        }
                        title={act.active ? "Désactiver" : "Réactiver"}
                        className={statusBadge(act.active)}
                      >
                        {act.active ? "Actif" : "Inactif"}
                      </button>
                    ) : (
                      <span className={statusBadge(act.active)}>{act.active ? "Actif" : "Inactif"}</span>
                    )}
                  </td>
                  {canEdit ? (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(act.code);
                          setDraft({ code: act.code, ...payloadOf(act) });
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-primary hover:bg-accent/20"
                      >
                        <Pencil size={13} />
                        Modifier
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
              {acts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-muted">
                    {data ? "Aucun code ne correspond." : "Chargement…"}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
